import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Overlander2InterestDialog({ triggerLabel = "Register interest", triggerTestId = "o2-open-btn" }) {
  const [open, setOpen] = useState(false);
  const [renderedAt, setRenderedAt] = useState(() => Date.now());
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    request_quote: false,
    company: "", // honeypot
  });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", request_quote: false, company: "" });
    setDone(false);
    setLoading(false);
    setRenderedAt(Date.now());
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) {
      // Reset on open so the counter starts fresh
      reset();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (Date.now() - renderedAt < 2500) {
      toast.error("Whoa there — slow down a sec and try again.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/overlander-2`, form);
      setDone(true);
      toast.success(
        form.request_quote
          ? "Quote request received — we'll be in touch within 3 business days."
          : "You're on the Overlander 2 waitlist. We'll be in touch when it lands.",
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = status === 429
        ? "Too many submissions. Please try again in an hour."
        : Array.isArray(detail) ? detail[0]?.msg : (detail || "Something went wrong. Please try again.");
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid={triggerTestId}
          variant="outline"
          className="w-full rounded-sm uppercase tracking-wider font-bold py-6 text-sm group"
        >
          {triggerLabel}
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="o2-interest-dialog"
        className="sm:max-w-lg rounded-sm border-border p-0 gap-0 overflow-hidden"
      >
        <div className="bg-foreground text-background px-7 py-6">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-primary">
            Overlander 2 · Coming soon
          </p>
          <DialogHeader className="mt-3 space-y-2 text-left">
            <DialogTitle className="font-display text-2xl md:text-3xl uppercase tracking-tight font-black leading-[1.1]">
              Put your hand up for the Overlander 2.
            </DialogTitle>
            <DialogDescription className="text-background/70 text-sm leading-relaxed">
              Drop your details and we&apos;ll let you know the moment stock lands. Tick the box below if you want a full quote sent through when it&apos;s ready.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div data-testid="o2-success" className="px-7 py-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
            <h3 className="mt-4 font-display text-xl md:text-2xl font-bold uppercase tracking-tight">
              {form.request_quote ? "Quote coming your way." : "You're on the list."}
            </h3>
            <p className="mt-3 text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
              {form.request_quote
                ? "A specialist will be in touch with a full Overlander 2 quote within three business days."
                : "We'll email you as soon as the Overlander 2 is ready to order."}
            </p>
            <Button
              data-testid="o2-close-btn"
              onClick={() => setOpen(false)}
              variant="outline"
              className="mt-6 rounded-sm uppercase tracking-wider font-bold"
            >
              Close
            </Button>
          </div>
        ) : (
          <form data-testid="o2-form" onSubmit={submit} className="px-7 py-6 space-y-5">
            {/* Honeypot */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={set("company")}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              className="hidden"
            />

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Full name <span className="text-primary">*</span>
              </Label>
              <Input
                data-testid="o2-input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Citizen"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Email <span className="text-primary">*</span>
              </Label>
              <Input
                data-testid="o2-input-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jane@example.com.au"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Mobile <span className="text-primary">*</span>
              </Label>
              <Input
                data-testid="o2-input-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="04xx xxx xxx"
                className="rounded-sm"
              />
            </div>

            <label
              htmlFor="o2-request-quote"
              className="flex items-start gap-3 rounded-sm border border-border bg-muted/40 p-4 cursor-pointer hover:bg-muted transition-colors"
            >
              <Checkbox
                id="o2-request-quote"
                data-testid="o2-request-quote"
                checked={form.request_quote}
                onCheckedChange={(v) => setForm((f) => ({ ...f, request_quote: !!v }))}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <div className="text-sm font-semibold text-foreground leading-tight">
                  I&apos;d like a full quote when it&apos;s ready.
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  A specialist will send pricing, options and funding pathways within three business days.
                </div>
              </div>
            </label>

            <Button
              data-testid="o2-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-sm uppercase tracking-wider font-bold py-6 text-sm group"
            >
              {loading ? "Sending…" : "Register my interest"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
